import { randomUUID } from "expo-crypto";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "@utils/trpc";
import sdk from "@farcaster/miniapp-sdk";
import { useFarcasterContext } from "@hooks/useFarcasterContext";
import { capabilities } from "../capabilities";

const BASE_USDC_TOKEN = process.env.EXPO_PUBLIC_BASE_USDC_TOKEN ?? "";
const LISSEN_BASE_WALLET_ADDRESS =
  process.env.EXPO_PUBLIC_LISSEN_BASE_WALLET_ADDRESS ?? "";

/**
 * Hook for Base USDC payments in Farcaster Mini App.
 *
 * WEB VERSION: Uses the Farcaster SDK sendToken action to initiate payments.
 *
 * Flow:
 * 1. Freeze the price on the backend
 * 2. Call Farcaster SDK sendToken to initiate the USDC transfer
 * 3. Return the transaction hash for verification
 */
const useBasePayment = () => {
  const { isInMiniApp } = useFarcasterContext();
  const isCryptoCheckoutAllowed = capabilities.allowed("web3.mobileCheckout");
  const freezePrice = trpc.accesses.freezeBasePrice.useMutation();

  const sendTransaction = useMutation({
    mutationFn: async ({
      usdcAmount,
      accessId,
      freezeId,
    }: {
      usdcAmount: number;
      accessId: string;
      freezeId: string;
    }): Promise<
      | {
          success: true;
          transactionHash: string;
        }
      | {
          success: false;
          error?: string;
        }
    > => {
      if (!isCryptoCheckoutAllowed) {
        return {
          success: false,
          error: "Crypto checkout is unavailable in preview environments",
        };
      }

      try {
        // Step 1: Freeze the price on the backend
        await freezePrice.mutateAsync({
          freezeId,
          accessId,
          usdcAmount,
        });

        // Step 2: Call Farcaster SDK to send USDC
        const result = await sdk.actions.sendToken({
          token: BASE_USDC_TOKEN,
          amount: usdcAmount.toString(),
          recipientAddress: LISSEN_BASE_WALLET_ADDRESS,
        });

        if (result.success) {
          return {
            success: true,
            transactionHash: result.send.transaction,
          };
        } else {
          // Handle user rejection or send failure
          const errorMessage =
            result.reason === "rejected_by_user"
              ? "Transaction cancelled"
              : "Transaction failed";
          return {
            success: false,
            error: errorMessage,
          };
        }
      } catch (error) {
        console.error("Base payment error:", error);
        return {
          success: false,
          error: "Failed to send transaction",
        };
      }
    },
  });

  return {
    sendTransaction: (input: { accessId: string; usdcAmount: number }) =>
      sendTransaction.mutateAsync({ ...input, freezeId: randomUUID() }),
    isLoading: sendTransaction.isPending || freezePrice.isPending,
    isAvailable: isCryptoCheckoutAllowed && isInMiniApp === true,
  };
};

export default useBasePayment;
